// src/Utils/navigationUtils.js

export const MOBILE_ALLOWED_KEYS = [
  "home",
  "etatyotila",
  "sauna",
  "announcements",
  "servicerequests",
];

/**
 * Tarkistaa näkyykö navigaatio-item käyttäjälle valitussa yhtiössä.
 */
export function canSeeNavigationItem({ item, user, selectedCompanyId }) {
  if (!item?.enabled) return false;
  if (!user) return false;

  // User-kohtainen näkyvyys menee roolien edelle
  if (Array.isArray(item.visibleForUsers) && item.visibleForUsers.length > 0) {
    return item.visibleForUsers.includes(user.uid);
  }

  // Roolipohjainen näkyvyys
  if (Array.isArray(item.visibleForRoles) && item.visibleForRoles.length > 0) {
    const roles = user.userRoles || [];

    return roles.some(
      (r) =>
        r.companyId === selectedCompanyId &&
        item.visibleForRoles.includes((r.role || "").toLowerCase())
    );
  }

  // Ei rajoitteita → näkyy
  return true;
}

/**
 * Palauttaa kaikki käyttäjälle näkyvät navigaatio-itemit configin perusteella.
 */
export function getVisibleNavigationItems({
  enabledItems,
  user,
  selectedCompanyId,
}) {
  if (!user) return [];

  return (enabledItems || []).filter((item) =>
    canSeeNavigationItem({
      item,
      user,
      selectedCompanyId,
    })
  );
}

/**
 * Palauttaa mobiilissa näytettävät navigaatio-itemit.
 * Näytetään vain ne itemit, jotka:
 * 1) ovat jo muuten näkyviä käyttäjälle
 * 2) kuuluvat kovakoodattuun mobiililistaan
 */
export function getMobileNavigationItems(items) {
  return (items || []).filter((item) => MOBILE_ALLOWED_KEYS.includes(item.key));
}

/**
 * Hakee aktiivisen navigaatio-itemin nykyisen pathname:n perusteella.
 *
 * Logiikka:
 * 1) ensin tarkka route-match
 * 2) jos ei löydy, etsitään pisin prefix-match
 *
 * Tämä mahdollistaa esim.
 * /settings/baseinformation -> /settings
 */
export function getActiveNavigationItem({ items, pathname }) {
  if (!Array.isArray(items) || !pathname) return null;

  // 1. tarkka match
  const exactMatch = items.find((item) => item.route === pathname);
  if (exactMatch) return exactMatch;

  // 2. prefix match, pisin route voittaa
  const prefixMatches = items
    .filter(
      (item) =>
        typeof item.route === "string" &&
        item.route !== "/" &&
        pathname.startsWith(item.route)
    )
    .sort((a, b) => b.route.length - a.route.length);

  if (prefixMatches.length > 0) {
    return prefixMatches[0];
  }

  // 3. fallback etusivulle jos pathname on "/"
  if (pathname === "/") {
    const rootItem = items.find((item) => item.route === "/");
    if (rootItem) return rootItem;

    const frontpageItem = items.find((item) => item.route === "/frontpage");
    if (frontpageItem) return frontpageItem;
  }

  return null;
}

/**
 * Palauttaa aktiivisen sivun otsikon turvallisesti.
 * Ensin yritetään mobiili-itemeistä, sitten kaikista näkyvistä itemeistä.
 */
export function getCurrentNavigationLabel({
  visibleItems,
  mobileItems,
  pathname,
  fallback = "AsukasHuudi",
}) {
  const activeFromMobile = getActiveNavigationItem({
    items: mobileItems,
    pathname,
  });

  if (activeFromMobile?.label) return activeFromMobile.label;

  const activeFromAll = getActiveNavigationItem({
    items: visibleItems,
    pathname,
  });

  if (activeFromAll?.label) return activeFromAll.label;

  return fallback;
}